/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Input,
  Tooltip,
  Col,
  Grid,
  message,
  Modal,
} from 'antd';
import styled from 'styled-components';
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CopyOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Select, { defRender } from 'components/Select';
import { capitalize } from 'utils/normalizers';
import { logger } from 'utils/debug';

const { confirm } = Modal;

const showConfirm = (e, resultProps) => {
  confirm({
    title: `¿Está seguro que desea retirar ${resultProps.label}?`,
    icon: <ExclamationCircleOutlined />,
    content: resultProps.contentConfirm,
    onOk() {
      message.success('Se ha retiró correctamente');
      e.stopPropagation();
      resultProps.onClearConfirm(e);
    },
    onCancel() {
      // console.log('Cancel');
    },
  });
};

const InputSearch = ({
  disabled,
  label,
  name,
  onChange,
  onClickAdvanced,
  rules,
  title,
  setVisibleBusqueda,
  onPressEnter,
  dataSelect,
  dataSelectLabel,
  inputProps,
  onChangeSelect,
  selectRules,
  normalizer,
}) => (
  <Form.Item noStyle style={{ marginBottom: '0px !important' }}>
    <Input.Group style={{ display: 'flex' }} compact>
      {!!dataSelect.length && (
        <Form.Item
          name="content_type"
          style={{
            marginTop: 'auto',
            width: '45%',
            minWidth: 180,
          }}
          rules={selectRules}
          label={dataSelectLabel}
        >
          <Select
            style={{ width: '100%' }}
            dataSource={dataSelect}
            render={(e) => e.model}
            onChange={onChangeSelect}
          />
        </Form.Item>
      )}
      <Form.Item
        style={{ width: '100%' }}
        normalize={normalizer}
        name={name}
        label={(
          <span>
            {label}
            {title && (
              <>
                {' '}
                <Tooltip title={title}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </>
            )}
          </span>
        )}
        rules={rules}
      >
        <Input
          allowClear
          onChange={onChange}
          disabled={disabled}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...inputProps}
          onPressEnter={(e) => {
            if (e) {
              e.preventDefault();
            }
            onPressEnter(name);
            if (inputProps.onPressEnter) {
              inputProps.onPressEnter(name);
            }
          }}
        />
      </Form.Item>
      <Form.Item>
        <Button
          disabled={disabled}
          style={{
            marginTop: 23,
          }}
          onClick={() => {
            onPressEnter(name);
            if (inputProps.onPressEnter) {
              inputProps.onPressEnter(name);
            }
          }}
        >
          <SearchOutlined />
        </Button>
      </Form.Item>
      {!!onClickAdvanced && (
        <Form.Item className="primary-on-hover" label="">
          <Button
            onClick={() => {
              if (onClickAdvanced) {
                onClickAdvanced();
              } else if (setVisibleBusqueda) {
                setVisibleBusqueda(true);
              }
            }}
            disabled={disabled}
            style={{ marginTop: 23 }}
          >
            ...
          </Button>
        </Form.Item>
      )}

    </Input.Group>
  </Form.Item>
);

InputSearch.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  name: PropTypes.string,
  onChange: PropTypes.func,
  onClickAdvanced: PropTypes.func,
  rules: PropTypes.arrayOf(PropTypes.shape()),
  title: PropTypes.string,
  setVisibleBusqueda: PropTypes.func,
  onPressEnter: PropTypes.func,
  dataSelect: PropTypes.arrayOf(PropTypes.shape()),
  selectRules: PropTypes.arrayOf(PropTypes.shape()),
  dataSelectLabel: PropTypes.string,
  inputProps: PropTypes.shape(),
  onChangeSelect: PropTypes.func,
  normalizer: PropTypes.func,
};

InputSearch.defaultProps = {
  disabled: false,
  label: null,
  name: 'q',
  onChange: null,
  onClickAdvanced: null,
  rules: [],
  title: '',
  setVisibleBusqueda: () => { },
  onPressEnter: () => { },
  dataSelect: [],
  dataSelectLabel: null,
  inputProps: {},
  onChangeSelect: () => { },
  selectRules: [],
  normalizer: () => { },
};

const defBreakpoints = { xs: 24, sm: 24, md: 8 };

const getBreakPoints = (dataSource, rBreakPoints, breakPoints) => {
  if (Array.isArray(dataSource) ? dataSource.length : dataSource) {
    return { ...defBreakpoints, ...rBreakPoints } || defBreakpoints;
  }
  return { ...defBreakpoints, ...breakPoints };
};

export const InputSearchPro = ({
  disabled,
  label,
  name,
  rules,
  onClickAdvanced,
  onPressEnter,
  inputProps,
  prefix,
  tooltip,
  resultProps,
  breakPoints,
  order,
  copyableResult,
  advancedComponent: AdvancedSearch,
  advancedComponentProps,
  getter,
  disabledOnResult,
  qName,
  callback: _callback,
  params: _params,
  allowDataAsObject,
  setLoading: _setLoading,
}) => {
  const form = Form.useFormInstance();
  const screens = Grid.useBreakpoint();
  const [loading, setLoading] = useState(false);
  const [visibleAdvanced, setVisibleAdvanced] = useState(false);
  const [_dataSource, setDataSource] = useState();

  const changeLoading = (val) => {
    if (_setLoading) _setLoading(val);
    setLoading(val);
  };

  const dataSource = _dataSource || resultProps.dataSource;
  const showResult = Array.isArray(dataSource) ? !!dataSource.length : !!dataSource;
  const searchBreakpoints = !showResult
    ? getBreakPoints(dataSource, resultProps.breakPoints, breakPoints) : { span: 0 };
  const resultBreakpoints = showResult
    ? getBreakPoints(dataSource, resultProps.breakPoints, breakPoints) : { span: 0 };

  const callback = (record) => {
    _callback(record);
    form.setFieldValue(resultProps.name, record[resultProps.valueProp || 'id']);
    form.setFieldValue(name, null);
    form.validateFields([resultProps.name]);
    if (resultProps.setDataSource) {
      resultProps.setDataSource(record);
    } else {
      setDataSource(record);
    }
  };

  const handleSearch = async () => {
    try {
      await form.validateFields([name]);
      const q = form.getFieldValue(name);
      if (!q) return;
      changeLoading(true);
      const params = {
        ...advancedComponentProps?.extraParams,
        ..._params,
        [qName || name || resultProps.labelProp]: q,
      };
      const data = await getter(params);
      const _data = data?.results || data;
      if (_data.length === 1 || (allowDataAsObject && _data)) {
        const [selected] = allowDataAsObject ? [data] : _data;
        callback(selected);
      } else {
        message.info(resultProps.notFoundMessage || `No se encontró ${resultProps.label}`);
      }
      changeLoading(false);
    } catch (err) {
      logger(err);
    }
  };

  // useEffect(() => {
  //   if (form.getFieldValue(name)) handleSearch();
  // }, []);

  const Prefix = () => !!prefix && (
    <Form.Item
      name={prefix.name}
      style={{
        ...prefix.style,
        width: !screens.md ? '100%' : prefix?.style?.width,
      }}
      rules={prefix.rules}
      label={prefix.label}
      hidden={prefix.hidden}
    >
      <Select
        style={{ width: !screens.md ? '100%' : prefix?.style?.width }}
        dataSource={prefix.dataSource}
        render={prefix.render}
        onChange={prefix.onChange}
        disabled={disabled || prefix.disabled}
        allowClear={prefix.allowClear}
      />
    </Form.Item>
  );

  const SearchBox = () => (
    <Form.Item
      style={{ width: '100%' }}
      name={name}
      normalize={(value) => (inputProps?.normalize || capitalize)(value)}
      label={(
        <span>
          {label}
          {tooltip && (
            <>
              {' '}
              <Tooltip title={tooltip}>
                <QuestionCircleOutlined />
              </Tooltip>
            </>
          )}
        </span>
      )}
      rules={showResult ? [] : rules}
    >
      <Input
        allowClear
        {...inputProps}
        onPressEnter={(e) => {
          e.preventDefault();
          if (inputProps.onPressEnter) {
            inputProps.onPressEnter(name);
          } else {
            handleSearch();
          }
        }}
        disabled={disabled || inputProps.disabled || loading}
      />
    </Form.Item>
  );

  const SearchButton = () => (
    <Form.Item>
      <Button
        disabled={disabled || loading}
        style={{
          marginTop: 23,
        }}
        onClick={() => {
          if (getter) {
            handleSearch();
          }
          if (onPressEnter) {
            onPressEnter(name);
          }
          if (inputProps.onPressEnter) {
            inputProps.onPressEnter(name);
          }
        }}
        id={`search_button_${name}`}
        loading={loading}
      >
        <SearchOutlined />
      </Button>
    </Form.Item>
  );

  const AdvancedButton = () => (!!onClickAdvanced || AdvancedSearch) && (
    <Form.Item className="primary-on-hover">
      <Button
        onClick={() => {
          if (onClickAdvanced) {
            onClickAdvanced();
          }
          if (AdvancedSearch) {
            setVisibleAdvanced(true);
          }
        }}
        disabled={disabled || loading}
        style={{ marginTop: 23 }}
        id={`onclick-advanced-search-${name}`}
      >
        <Tooltip title="Búsqueda avanzada">
          ...
        </Tooltip>
      </Button>
    </Form.Item>
  );

  const keyLabelRenderFunc = (item) => `${item[resultProps.keyProp]} - ${item[resultProps.labelProp]}`;

  const getText = (obj) => {
    if (resultProps.keyLabelRender) {
      return keyLabelRenderFunc(obj);
    }
    if (resultProps.render) {
      return resultProps.render(obj);
    }
    if (resultProps.labelProp) {
      return defRender(obj[resultProps.labelProp]);
    }
    return defRender(obj);
  };

  const allowClear = !!resultProps.onClear || !!AdvancedSearch || (!!getter && !disabled);

  return (
    <>
      <Col {...resultBreakpoints} order={order}>
        <FormItemRelative>
          <Form.Item
            name={resultProps.name}
            label={resultProps.label}
            rules={showResult || resultProps?.rules?.[0].required ? resultProps.rules : []}
          >
            <Select
              dataSource={(Array.isArray(dataSource) ? dataSource : [dataSource])}
              keyLabelRender={!!resultProps.keyLabelRender}
              valueProp={resultProps.valueProp}
              keyProp={resultProps.keyProp}
              labelProp={resultProps.labelProp}
              render={resultProps.render}
              async={resultProps.async}
              onChange={resultProps.onChange}
              disabled={disabledOnResult}
              allowClear={false}
              showArrow={false}
            />
          </Form.Item>
          {resultProps.onClearConfirm && !disabled && (
            <CloseCircleOutlined onClick={(e) => showConfirm(e, resultProps, name)} />
          )}
          {allowClear && !disabled && (
            <CloseCircleOutlined
              onClick={(e) => {
                e.stopPropagation();
                if (resultProps.onClear) resultProps.onClear(e);
                if (getter || AdvancedSearch) {
                  form.resetFields([resultProps.name, name]);
                  setDataSource();
                }
              }}
            />
          )}
          {copyableResult && (
            <CopyOutlined
              onClick={(e) => {
                e.stopPropagation();
                const obj = Array.isArray(dataSource) ? dataSource[0] : dataSource;
                const txt = getText(obj);
                navigator.clipboard.writeText(txt);
                message.info('Texto copiado');
              }}
              style={{ right: (allowClear || resultProps.onClearConfirm) && !disabled ? 30 : 5 }}
            />
          )}
        </FormItemRelative>
      </Col>
      <Col {...searchBreakpoints} order={order}>
        {screens.xs ? (
          <Form.Item noStyle>
            <Prefix />
            <Input.Group style={{ display: 'flex' }} compact>
              <SearchBox />
              <SearchButton />
              <AdvancedButton />
            </Input.Group>
          </Form.Item>
        ) : (
          <Input.Group style={{ display: 'flex' }} compact>
            <Prefix />
            <SearchBox />
            <SearchButton />
            <AdvancedButton />
          </Input.Group>
        )}
      </Col>
      {AdvancedSearch && (
        <AdvancedSearch
          visible={visibleAdvanced}
          setVisible={(_visible) => setVisibleAdvanced(_visible)}
          {...advancedComponentProps}
          callback={(record) => {
            if (advancedComponentProps.callback) advancedComponentProps.callback(record);
            callback(record);
            setDataSource([record]);
            form.setFieldsValue({ object_id_padron: record.id });
          }}
        />
      )}
    </>
  );
};

const FormItemRelative = styled.div`
  position: relative;
  width: 100%;
  .anticon-close-circle, .anticon-copy {
    position: absolute;
    top: 34px;
    z-index: 2;
  }
  .anticon-close-circle {
    right: 10px;
  }
`;

InputSearchPro.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  rules: PropTypes.arrayOf(PropTypes.shape()),
  onClickAdvanced: PropTypes.func,
  onPressEnter: PropTypes.func,
  inputProps: PropTypes.shape(),
  prefix: PropTypes.shape(),
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  resultProps: PropTypes.shape(),
  breakPoints: PropTypes.shape(),
  order: PropTypes.number,
  copyableResult: PropTypes.bool,
  advancedComponent: PropTypes.func,
  getter: PropTypes.func,
  advancedComponentProps: PropTypes.shape({
    extraParams: PropTypes.shape({}),
    callback: PropTypes.func,
  }),
  disabledOnResult: PropTypes.bool,
  qName: PropTypes.string,
  callback: PropTypes.func,
  params: PropTypes.shape({}),
  allowDataAsObject: PropTypes.bool,
  setLoading: PropTypes.func,
};

InputSearchPro.defaultProps = {
  disabled: false,
  label: null,
  name: 'q',
  onClickAdvanced: null,
  rules: [],
  onPressEnter: () => { },
  inputProps: {},
  prefix: null,
  tooltip: null,
  resultProps: null,
  breakPoints: {},
  order: null,
  copyableResult: true,
  advancedComponent: null,
  getter: null,
  advancedComponentProps: null,
  disabledOnResult: true,
  qName: null,
  callback: () => null,
  params: {},
  allowDataAsObject: false,
  setLoading: null,
};

export default InputSearch;
